// RiskEventsTab — lists events linked to the current risk.
// Data comes from GET /risks/{risk_id}/events.
// Each row links to the event detail page.

import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { risksApi, type LinkedEventSummary } from '@/lib/api/risks'
import {
  eventLifecycleStateLabels,
  eventLifecycleStateColors,
} from '@/lib/api/events'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Zap, FolderOpen, Calendar, User, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'

export function RiskEventsTab() {
  const { slug } = useTenant()
  const { riskId } = useParams({ strict: false }) as { riskId: string }
  const navigate = useNavigate()
  const api = useApi()
  const riskApi = risksApi(api)

  const {
    data: events,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.risks.events(slug, riskId),
    queryFn: () => riskApi.events(riskId),
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load linked events"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!events || events.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        <div className="space-y-2 text-center">
          <Zap className="mx-auto h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm">No events are linked to this risk.</p>
          <p className="text-xs text-muted-foreground/60">
            Events can be linked from the Events section.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-3 text-sm text-muted-foreground">
        {events.length} linked event{events.length !== 1 ? 's' : ''}
      </div>
      <div className="divide-y rounded-lg border">
        {events.map((event) => (
          <EventRow
            key={event.link_id}
            event={event}
            onClick={() => navigate({ to: `/t/${slug}/events/${event.event_id}` })}
          />
        ))}
      </div>
    </div>
  )
}

function EventRow({
  event,
  onClick,
}: {
  event: LinkedEventSummary
  onClick: () => void
}) {
  return (
    <div
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/30"
      onClick={onClick}
    >
      {/* Title + description */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{event.title}</span>
          <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
        </div>
        {event.description && (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {event.description}
          </div>
        )}
      </div>

      {/* Event type */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground md:flex">
        <Zap className="h-3 w-3" />
        <span className="max-w-[120px] truncate">{event.event_type_name}</span>
      </div>

      {/* Category */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground lg:flex">
        <FolderOpen className="h-3 w-3" />
        <span className="max-w-[120px] truncate">{event.category_name}</span>
      </div>

      {/* Event date */}
      {event.event_date && (
        <div className="hidden items-center gap-1 text-xs text-muted-foreground xl:flex">
          <Calendar className="h-3 w-3" />
          <span>{new Date(event.event_date).toLocaleDateString()}</span>
        </div>
      )}

      {/* Recorder */}
      {event.recorded_by_name && (
        <div className="hidden items-center gap-1 text-xs text-muted-foreground xl:flex">
          <User className="h-3 w-3" />
          <span className="max-w-[100px] truncate">{event.recorded_by_name}</span>
        </div>
      )}

      {/* Status badge */}
      <Badge
        variant="outline"
        className={cn(
          'flex-shrink-0 px-1.5 py-0 text-xs',
          eventLifecycleStateColors[event.lifecycle_state] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {eventLifecycleStateLabels[event.lifecycle_state] ?? event.lifecycle_state}
      </Badge>
    </div>
  )
}
